import { cn } from "@/lib/utils";
import { Crown } from "lucide-react";
import {
  accentClasses,
  segmentClasses,
  type IndustryAccent,
} from "../industryTheme/industryTheme.util";
import type { MiniUi } from "./miniUiPreview.type";

interface MiniUiPreviewProps {
  ui: MiniUi;
  accent?: IndustryAccent;
  className?: string;
}

const keyClass =
  "text-[12px] font-bold tracking-[0.06em] text-ink-muted uppercase";
const bigClass = "text-[1.5rem] leading-[1.1] font-extrabold tracking-[-0.5px]";
const smallClass = "text-[12px] text-ink-muted";
const chipClass = "rounded-[10px] px-3 py-2 text-[12.5px] font-bold";
const pillClass = "rounded-full px-3 py-1 text-[12.5px] font-bold";

// One of the preview's small floating UI cards (`.mu`), e.g. "Cashback
// added +₹24", a reward-code ticket or a WhatsApp bubble. Sits on a soft
// background inside a feature card; wrap it in a `group` to get the
// preview's lift-and-tilt on card hover.
const MiniUiPreview = ({ ui, accent = "brand", className }: MiniUiPreviewProps) => {
  const tone = accentClasses[accent];

  const bar = (progress: number) => (
    <span
      aria-hidden="true"
      className="block h-2 w-full overflow-hidden rounded-full bg-line"
    >
      <span
        className={cn("block h-full rounded-full", tone.solid)}
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </span>
  );

  const body = () => {
    switch (ui.kind) {
      case "cash":
        return (
          <>
            <span className={keyClass}>{ui.label}</span>
            <b className={bigClass}>{ui.value}</b>
            {bar(ui.progress)}
            <small className={smallClass}>{ui.note}</small>
          </>
        );
      case "goal":
        return (
          <>
            <span className={keyClass}>{ui.label}</span>
            <b className={bigClass}>
              {ui.value} <small className={smallClass}>{ui.of}</small>
            </b>
            {bar(ui.progress)}
            <small className={smallClass}>{ui.note}</small>
          </>
        );
      case "banner":
        return (
          <>
            <small className="text-[12px] text-white/80">{ui.kicker}</small>
            <b className={bigClass}>{ui.title}</b>
          </>
        );
      case "bubble":
        return (
          <>
            <span className="rounded-[14px] rounded-bl-[3px] bg-tone-mint px-[11px] py-[7px] text-[13px] font-semibold">
              {ui.message}
            </span>
            <small className={cn(smallClass, "justify-self-end")}>{ui.time}</small>
          </>
        );
      case "ticket":
        return (
          <>
            <span className={keyClass}>{ui.label}</span>
            <b className={cn(bigClass, "text-[1.35rem] tracking-[0.06em]", tone.text)}>
              {ui.code}
            </b>
            <small className={smallClass}>{ui.note}</small>
          </>
        );
      case "stars":
        return (
          <>
            <b
              aria-hidden="true"
              className="text-[1.3rem] leading-none tracking-[2px] text-tone-sun-ink"
            >
              ★★★★★
            </b>
            <span className={cn(pillClass, "bg-ink px-3.5 py-1.5 text-white")}>
              {ui.button}
            </span>
          </>
        );
      case "vip":
        return (
          <>
            <span className={cn("grid size-10 place-items-center rounded-xl", tone.soft)}>
              <Crown className={cn("size-[22px]", tone.text)} strokeWidth={1.8} aria-hidden="true" />
            </span>
            <b className={bigClass}>{ui.title}</b>
            <small className={smallClass}>{ui.note}</small>
          </>
        );
      case "calendar":
        return (
          <>
            <span aria-hidden="true" className="grid grid-cols-[repeat(7,12px)] gap-1.5">
              {Array.from({ length: 14 }, (_, index) => (
                <i
                  key={index}
                  className={cn(
                    "size-3 rounded-full",
                    index === (ui.day ?? 9)
                      ? cn(tone.solid, "ring-4", tone.ring)
                      : "bg-line",
                  )}
                />
              ))}
            </span>
            <span className="rounded-[14px] rounded-bl-[3px] bg-tone-mint px-[11px] py-[7px] text-[13px] font-semibold">
              {ui.message}
            </span>
          </>
        );
      case "tiers":
        return (
          <span className="flex gap-2">
            {ui.tiers.map((tier, index) => (
              <i
                key={tier}
                className={cn(
                  "rounded-xl px-3 py-2.5 text-[12px] font-extrabold text-ink not-italic",
                  ["bg-tier-bronze", "bg-tier-silver", "bg-tier-gold"][index],
                )}
              >
                {tier}
              </i>
            ))}
          </span>
        );
      case "stamps":
        return (
          <>
            <span aria-hidden="true" className="flex gap-[7px]">
              {Array.from({ length: ui.total }, (_, index) => (
                <i
                  key={index}
                  className={cn(
                    "grid size-6 place-items-center rounded-full border-[2.5px] text-[12px] font-extrabold text-white not-italic",
                    index < ui.filled
                      ? cn(tone.solid, tone.border)
                      : "border-dashed border-line",
                  )}
                >
                  {index < ui.filled ? "✓" : ""}
                </i>
              ))}
            </span>
            <small className={smallClass}>{ui.note}</small>
            <b className="text-[1.1rem] leading-[1.1] font-extrabold">{ui.title}</b>
          </>
        );
      case "clock":
        return (
          <>
            <span className={keyClass}>{ui.label}</span>
            <b className={bigClass}>{ui.value}</b>
            <span className={cn(pillClass, tone.soft, tone.text)}>{ui.tag}</span>
          </>
        );
      case "import":
        return (
          <span className="flex items-center gap-2.5 font-bold">
            <span className={cn(chipClass, "bg-paper")}>{ui.file}</span>
            <b aria-hidden="true" className="text-[1.2rem]">
              →
            </b>
            <span className={cn(chipClass, "bg-tone-mint text-tone-mint-ink")}>
              {ui.done}
            </span>
          </span>
        );
      case "split":
        return (
          <>
            <span className={keyClass}>{ui.label}</span>
            <span className="flex w-full gap-1 text-[12px] font-bold">
              <i
                className={cn(
                  "flex-[1.3] rounded-lg border-2 p-2 text-center not-italic",
                  tone.soft,
                  tone.text,
                  tone.border,
                )}
              >
                {ui.highlighted}
              </i>
              <i className="flex-1 rounded-lg bg-paper p-2 text-center text-ink-muted not-italic">
                {ui.other}
              </i>
            </span>
          </>
        );
      case "segments":
        return (
          <span className="grid grid-cols-[repeat(2,auto)] gap-x-3.5 gap-y-2 text-[13px] font-bold">
            {ui.items.map((item) => (
              <span key={item.label} className="flex items-center gap-[7px]">
                <i
                  aria-hidden="true"
                  className={cn("inline-block size-3 rounded-full", segmentClasses[item.segment].solid)}
                />
                {item.label}
              </span>
            ))}
          </span>
        );
      case "points":
        return (
          <>
            <span className={keyClass}>{ui.label}</span>
            <b className={bigClass}>{ui.value}</b>
            <small className={smallClass}>{ui.note}</small>
          </>
        );
      case "counter":
        return (
          <>
            <span className={keyClass}>{ui.label}</span>
            <b className="text-[1.2rem] leading-[1.1] font-extrabold">{ui.value}</b>
            <span className="font-bold">{ui.who}</span>
            <small className={smallClass}>{ui.note}</small>
            <span className={cn(pillClass, tone.soft, tone.text)}>{ui.tag}</span>
          </>
        );
      case "receipt":
        return (
          <>
            {ui.rows.map((row) => (
              <span
                key={row.label}
                className="flex w-full justify-between border-b border-dashed border-line py-1.5"
              >
                <span>{row.label}</span>
                <b className="text-base font-extrabold">{row.value}</b>
              </span>
            ))}
            <span className="mt-1 flex w-full justify-between border-t-2 border-ink py-1.5 font-extrabold">
              <span>{ui.total.label}</span>
              <b className={cn("text-base font-extrabold", tone.text)}>
                {ui.total.value}
              </b>
            </span>
          </>
        );
    }
  };

  return (
    <div
      className={cn(
        "relative z-[1] grid max-w-full min-w-[150px] justify-items-start gap-1.5 rounded-[18px] px-4 py-3.5 text-[13px] leading-[1.3] text-ink shadow-card transition-transform duration-500 ease-out group-hover:scale-105 group-hover:-rotate-[1.5deg] motion-reduce:transition-none motion-reduce:group-hover:scale-100 motion-reduce:group-hover:rotate-0",
        ui.kind === "banner"
          ? cn(tone.solid, "min-w-[170px] text-white")
          : ui.kind === "bubble"
            ? "max-w-[230px] bg-cream"
            : "bg-white",
        ui.kind === "ticket" && cn("border-2 border-dashed", tone.border),
        ui.kind === "points" && "border-2 border-dashed border-line",
        ui.kind === "receipt" && "min-w-[190px] gap-0",
        className,
      )}
    >
      {body()}
    </div>
  );
};

export default MiniUiPreview;
